import styles from "./ProductDescription.scss";
import PropTypes from "prop-types";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import SizeGuideButton, {
  SIZE_GUIDE_BUTTON_SIZE,
} from "../sizeGuideButton/SizeGuideButton";
import { NEW_USER } from "../../analytics/fitAssistantCommon";
import { useSizing } from "../../context/sizing/SizingContext";
import { showSizeAndFit, checkRecommendations } from "./sizeAndFitHelper";
import useFitAssistExperiment from "../fitAssistant/useFitAssistExperiment";
import { useOptionalMySizeProfile } from "../../context/mySizeProfile/MySizeProfileContext";

const getSizingTranslation = ({
  getTranslation,
  recommendedSize,
  isPartialRecommendation,
  isSuppressed,
}) => {
  if (!isSuppressed && recommendedSize === NEW_USER) {
    return getTranslation("pdp_fit_analytics_sizing_help_profile_none");
  }
  if (
    !isSuppressed &&
    recommendedSize !== NEW_USER &&
    isPartialRecommendation
  ) {
    return getTranslation("pdp_fit_analytics_sizing_help_profile_partial");
  }
  return getTranslation("pdp_fit_analytics_sizing_help_profile_complete");
};

const handleKeyPress = (handleFitAssistantLinkClick) => (event) => {
  if (event.key === "Enter") {
    handleFitAssistantLinkClick(event);
  }
};

const getSizingHelpText = ({
  getTranslation,
  recommendedSize,
  sizeGuideUrl,
  isPartialRecommendation,
  handleFitAssistantLinkClick,
  isSuppressed,
  isMySizeProfileEligible,
}) => {
  const introText = getTranslation("pdp_fit_analytics_sizing_help_intro");
  const sizingTranslation = getSizingTranslation({
    getTranslation,
    recommendedSize,
    isPartialRecommendation,
    isSuppressed,
  });

  const [fitAssistantLinkText, sizeGuideText] =
    sizingTranslation.split("%{splitPoint}");

  const [sizeGuideIntroText, sizeGuideButtonText] =
    sizeGuideText.split("%{link}");

  return (
    <p>
      {introText}
      {fitAssistantLinkText && !isSuppressed && !isMySizeProfileEligible && (
        <span
          role="button"
          tabIndex={0}
          data-testid="saf-fit-assistant"
          onClick={handleFitAssistantLinkClick}
          onKeyDown={handleKeyPress(handleFitAssistantLinkClick)}
          className={styles.link}
        >
          {" "}
          {fitAssistantLinkText}
        </span>
      )}
      {sizeGuideUrl && !isMySizeProfileEligible && (
        <>
          {" "}
          {sizeGuideIntroText}
          <SizeGuideButton
            sizeGuideUrl={sizeGuideUrl}
            text={sizeGuideButtonText.replace("%{linkEnd}", "")}
            size={SIZE_GUIDE_BUTTON_SIZE.LARGE}
            entryPoint="sizeguide-accordion"
          />
        </>
      )}
    </p>
  );
};

const showSizingHelp = ({
  sizeGuideUrl,
  recommendedSize,
  isPartialRecommendation,
}) =>
  sizeGuideUrl ||
  checkRecommendations({
    recommendedSize,
    isPartialRecommendation,
  });

export const SizeAndFit = ({ sizeAndFitVisible, sizeAndFit }) => {
  const { getTranslation } = useRuntimeConstants();
  const {
    sizeGuideUrl,
    recommendedSize,
    isPartialRecommendation,
    handleFitAssistantLinkClick,
    fitAssistantLinkText,
  } = useSizing();
  const {
    isEligible: isMySizeProfileEligible,
    isAuthenticated: isMySizeProfileAuthenticated,
  } = useOptionalMySizeProfile();
  const shouldSuppressSizingHelpForMySizeProfile =
    isMySizeProfileEligible && isMySizeProfileAuthenticated;
  const { isSuppressed } = useFitAssistExperiment({
    fitAssistEligible: !!fitAssistantLinkText,
    shouldSuppressForMySizeProfile: shouldSuppressSizingHelpForMySizeProfile,
  });

  if (
    !showSizeAndFit({
      sizeAndFitVisible,
      sizeGuideUrl,
      recommendedSize,
      isPartialRecommendation,
    })
  ) {
    return null;
  }

  const shouldShowSizingHelp = isSuppressed
    ? !!sizeGuideUrl
    : showSizingHelp({
        sizeGuideUrl,
        recommendedSize,
        isPartialRecommendation,
      });

  if (!sizeAndFitVisible && !shouldShowSizingHelp) {
    return null;
  }

  return (
    <>
      {sizeAndFitVisible && (
        <div
          className={styles.content}
          dangerouslySetInnerHTML={{ __html: sizeAndFit }}
        />
      )}
      {shouldShowSizingHelp && !shouldSuppressSizingHelpForMySizeProfile && (
        <div
          className={styles.content}
          data-testid="product-description-sizing-help"
        >
          <h3>{getTranslation("pdp_fit_analytics_sizing_help_title")}</h3>
          {getSizingHelpText({
            getTranslation,
            recommendedSize,
            sizeGuideUrl,
            isPartialRecommendation,
            handleFitAssistantLinkClick,
            isSuppressed,
            isMySizeProfileEligible: shouldSuppressSizingHelpForMySizeProfile,
          })}
        </div>
      )}
    </>
  );
};

SizeAndFit.propTypes = {
  sizeAndFitVisible: PropTypes.bool,
  sizeAndFit: PropTypes.string,
};
